// Commitment list + detail + mutation API — FE-041 / FE-043.
//
// Backend: BE-052 owns the canonical
// ``GET/POST/PATCH /api/v1/commitments/`` surface plus the action
// endpoints (``/transition``, ``/delete``, ``/assign``,
// ``/block`` / ``/unblock``, ``/tag`` / ``/untag``,
// ``/watch`` / ``/unwatch``). The frontend keeps Account / Commitment
// vocabulary; the request payload remaps ``account_id`` →
// ``client_id`` to match the BE-012 column name (BE-052 also accepts
// ``account_id`` as an inbound alias, but emitting ``client_id`` is
// the safer bet for forwards-compat).

import { ApiError, api } from "@/api/client";

import {
  CommitmentNotFoundError,
  type CommitmentDetail,
  type CommitmentListQuery,
  type CommitmentListResponse,
  type CommitmentMutationResponse,
  type CommitmentTransitionInput,
  type CreateCommitmentInput,
  type UpdateCommitmentInput,
} from "./types";


type Primitive = string | number | boolean;
type ParamValue = Primitive | undefined | null;


function buildQueryString(params: Record<string, ParamValue>): string {
  const entries: Array<[string, string]> = [];
  for (const key of Object.keys(params).sort()) {
    const value = params[key];
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "boolean") {
      entries.push([key, value ? "true" : "false"]);
      continue;
    }
    entries.push([key, String(value)]);
  }
  if (entries.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.append(k, v);
  return `?${sp.toString()}`;
}


export function fetchCommitments(
  query: CommitmentListQuery,
  opts?: { signal?: AbortSignal },
): Promise<CommitmentListResponse> {
  const qs = buildQueryString({
    search: query.filters.search,
    status: query.filters.status,
    priority: query.filters.priority,
    client_id: query.filters.client_id,
    plan_year_id: query.filters.plan_year_id,
    assigned_to: query.filters.assigned_to,
    category: query.filters.category,
    overdue: query.filters.overdue,
    is_blocked: query.filters.is_blocked,
    has_assignee: query.filters.has_assignee,
    has_due_date_time: query.filters.has_due_date_time,
    on_time: query.filters.on_time,
    snapshot_date: query.filters.snapshot_date,
    include_cancelled: query.filters.include_cancelled,
    archived: query.filters.archived,
    visibility: query.filters.visibility,
    source: query.filters.source,
    playbook_application_id: query.filters.playbook_application_id,
    source_touchpoint_id: query.filters.source_touchpoint_id,
    filter: query.filterTree
      ? JSON.stringify(query.filterTree)
      : undefined,
    sort_by: query.sortBy,
    sort_dir: query.sortDir,
    limit: query.limit,
    offset: query.offset,
  });
  return api.get<CommitmentListResponse>(
    `/api/v1/commitments/${qs}`,
    opts,
  );
}


// ── Detail / mutations ────────────────────────────────────────────────────


/** GET /api/v1/commitments/{id}. Throws ``CommitmentNotFoundError`` on
 * 404 so the page renders its dedicated empty-state without conflating
 * "not found" with "network error". */
export async function fetchCommitmentDetail(
  commitmentId: string,
  opts?: { signal?: AbortSignal },
): Promise<CommitmentDetail> {
  try {
    return await api.get<CommitmentDetail>(
      `/api/v1/commitments/${encodeURIComponent(commitmentId)}`,
      opts,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw new CommitmentNotFoundError(commitmentId);
    }
    throw err;
  }
}


/** POST /api/v1/commitments/. */
export function createCommitment(
  input: CreateCommitmentInput,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    "/api/v1/commitments/",
    normalizeAccountId(input),
  );
}


/** PATCH /api/v1/commitments/{id}. */
export function updateCommitment(
  commitmentId: string,
  input: UpdateCommitmentInput,
): Promise<CommitmentMutationResponse> {
  return api.patch<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}`,
    normalizeAccountId(input),
  );
}


// Frontend forms speak Account vocabulary; BE-012 stores ``client_id``.
// BE-052 accepts ``account_id`` as an alias on inbound, but emitting
// ``client_id`` is the safer bet for forwards-compat. Drop the alias
// before fetch so the same payload is sent regardless of which key
// the form pushed in.
function normalizeAccountId<T extends { account_id?: unknown; client_id?: unknown }>(
  input: T,
): T {
  if (input.account_id == null) return input;
  const { account_id, ...rest } = input as T & {
    account_id?: string | null;
  };
  if (rest.client_id != null) return rest as T;
  return { ...(rest as T), client_id: account_id } as T;
}


/** POST /api/v1/commitments/{id}/transition. */
export function transitionCommitment(
  commitmentId: string,
  input: CommitmentTransitionInput,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/transition`,
    input,
  );
}


/** POST /api/v1/commitments/{id}/delete. Soft delete (sets
 * ``deleted_at``). */
export function deleteCommitment(
  commitmentId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/delete`,
  );
}


/** POST /api/v1/commitments/{id}/block. */
export function blockCommitment(
  commitmentId: string,
  blocked_reason: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/block`,
    { blocked_reason },
  );
}


/** POST /api/v1/commitments/{id}/unblock. */
export function unblockCommitment(
  commitmentId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/unblock`,
  );
}


/** POST /api/v1/commitments/{id}/assign. BE enforces XOR — exactly
 * one of ``assigned_to`` (User), ``assigned_job_title`` (string
 * placeholder), ``assigned_client_contact_id`` is set. Pass nulls
 * across the board to clear assignment. */
export interface AssignCommitmentInput {
  assigned_to?: string | null;
  assigned_job_title?: string | null;
  assigned_client_contact_id?: string | null;
}


export function assignCommitment(
  commitmentId: string,
  input: AssignCommitmentInput,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/assign`,
    input,
  );
}


/** POST /api/v1/commitments/{id}/tag — add a tag to the row. */
export function tagCommitment(
  commitmentId: string, tagId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/tag`,
    { tag_id: tagId },
  );
}


/** POST /api/v1/commitments/{id}/untag — remove a tag from the row. */
export function untagCommitment(
  commitmentId: string, tagId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/untag`,
    { tag_id: tagId },
  );
}


/** POST /api/v1/commitments/{id}/watch — add a user as a watcher.
 * Body is optional; omitting ``user_id`` defaults to the caller. */
export function watchCommitment(
  commitmentId: string, userId?: string | null,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/watch`,
    userId ? { user_id: userId } : {},
  );
}


/** POST /api/v1/commitments/{id}/unwatch. */
export function unwatchCommitment(
  commitmentId: string, userId?: string | null,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/unwatch`,
    userId ? { user_id: userId } : {},
  );
}


/** POST /api/v1/commitments/{id}/review — toggle the reviewer
 * approval mark. Only the assigned reviewer can call this.
 * Setting ``reviewed=false`` clears the prior approval. */
export function reviewCommitment(
  commitmentId: string, reviewed: boolean,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/review`,
    { reviewed },
  );
}


/** POST /api/v1/commitments/{id}/dependency — adds a
 * finish-to-start dependency. */
export function addCommitmentDependency(
  commitmentId: string, dependsOnCommitmentId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/dependency`,
    { depends_on_commitment_id: dependsOnCommitmentId },
  );
}


/** POST /api/v1/commitments/{id}/dependency/remove. */
export function removeCommitmentDependency(
  commitmentId: string, dependencyId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/dependency/remove`,
    { dependency_id: dependencyId },
  );
}


/** POST /api/v1/commitments/{id}/archive — move the row into the
 * archive lifecycle. Idempotent. */
export function archiveCommitment(
  commitmentId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/archive`,
  );
}


/** POST /api/v1/commitments/{id}/restore — clear the archive
 * timestamp so the row reappears in default tracker views. */
export function restoreCommitment(
  commitmentId: string,
): Promise<CommitmentMutationResponse> {
  return api.post<CommitmentMutationResponse>(
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/restore`,
  );
}


/** POST /api/v1/accounts/{account_id}/plan_years/{plan_year_id}/close —
 * archive completed Commitments for that (Account, PlanYear) and
 * surface a warning for incomplete work. ``dry_run=true`` previews
 * without mutating. */
export interface PlanYearCloseInput {
  dry_run?: boolean;
}


export interface PlanYearCloseSummary {
  account_id: string;
  plan_year_id: string;
  archived_count: number;
  incomplete_count: number;
  archived_commitments: Array<{
    commitment_id: string;
    commitment_number: string;
    title: string;
    status: string;
  }>;
  incomplete_commitments: Array<{
    commitment_id: string;
    commitment_number: string;
    title: string;
    status: string;
    due_date_time: string | null;
    assigned_to: string | null;
  }>;
  warning: string | null;
  dry_run: boolean;
}


export function closePlanYear(
  accountId: string,
  planYearId: string,
  input: PlanYearCloseInput = {},
): Promise<PlanYearCloseSummary> {
  return api.post<PlanYearCloseSummary>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}` +
      `/plan_years/${encodeURIComponent(planYearId)}/close`,
    { dry_run: input.dry_run ?? false },
  );
}


// ── Saved filters + pickers (Polish B2) ───────────────────────────────────


export type SavedFilterVisibility = "personal" | "broker";


export interface SavedFilterDto {
  id: string;
  scope: string;
  name: string;
  filter_definition: unknown;
  is_default: boolean;
  sort_order: number;
  visibility: SavedFilterVisibility;
  /** Creator's user id; ``null`` only for legacy rows that pre-date
   * the ``created_by`` migration. The frontend gates Edit / Delete
   * affordances on ``created_by_id === currentUser.id``. */
  created_by_id: string | null;
  user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}


export interface SavedFilterListResponse {
  rows: SavedFilterDto[];
  total: number;
}


export function fetchSavedFilters(
  scope = "commitment",
  opts?: { signal?: AbortSignal },
): Promise<SavedFilterListResponse> {
  return api.get<SavedFilterListResponse>(
    `/api/v1/saved_filters/?scope=${encodeURIComponent(scope)}`,
    opts,
  );
}


export interface CreateSavedFilterInput {
  scope?: string;
  name: string;
  filter_definition: unknown;
  visibility?: SavedFilterVisibility;
}


export function createSavedFilter(
  input: CreateSavedFilterInput,
): Promise<SavedFilterDto> {
  return api.post<SavedFilterDto>("/api/v1/saved_filters/", {
    scope: input.scope ?? "commitment",
    name: input.name,
    filter_definition: input.filter_definition,
    visibility: input.visibility ?? "personal",
  });
}


export interface UpdateSavedFilterInput {
  name?: string;
  filter_definition?: unknown;
  visibility?: SavedFilterVisibility;
}


export function updateSavedFilter(
  id: string,
  input: UpdateSavedFilterInput,
): Promise<SavedFilterDto> {
  return api.patch<SavedFilterDto>(
    `/api/v1/saved_filters/${encodeURIComponent(id)}`,
    input,
  );
}


export function deleteSavedFilter(id: string): Promise<void> {
  return api.delete<void>(
    `/api/v1/saved_filters/${encodeURIComponent(id)}`,
  );
}


export interface MemberPickerRow {
  id: string;
  display_name: string;
  email: string;
  role: string;
}


export function fetchMembers(
  opts?: { signal?: AbortSignal },
): Promise<{ members: MemberPickerRow[] }> {
  return api.get<{ members: MemberPickerRow[] }>(
    "/api/v1/commitments/pickers/members",
    opts,
  );
}


export interface CategoryPickerRow {
  id: string;
  slug: string;
  display_name: string;
  display_color: string | null;
  display_icon: string | null;
  is_system: boolean;
}


export function fetchCategoryPicker(
  opts?: { signal?: AbortSignal },
): Promise<{ categories: CategoryPickerRow[] }> {
  return api.get<{ categories: CategoryPickerRow[] }>(
    "/api/v1/commitments/pickers/categories",
    opts,
  );
}


export interface TagPickerRow {
  id: string;
  slug: string;
  display_name: string;
  display_color: string | null;
  is_regulatory: boolean;
}


export function fetchTagPicker(
  opts?: { signal?: AbortSignal },
): Promise<{ tags: TagPickerRow[] }> {
  return api.get<{ tags: TagPickerRow[] }>(
    "/api/v1/commitments/pickers/tags",
    opts,
  );
}


export interface CreateTagInput {
  display_name: string;
  is_regulatory?: boolean;
  display_color?: string | null;
  slug?: string;
}


export interface CreatedTag extends TagPickerRow {}


/** POST /api/v1/commitments/pickers/tags/create — create-or-fetch
 * a tag scoped to the current entity. */
export function createTag(
  input: CreateTagInput,
): Promise<CreatedTag> {
  return api.post<CreatedTag>(
    "/api/v1/commitments/pickers/tags/create",
    input,
  );
}


export interface PlanYearPickerRow {
  id: string;
  account_id: string;
  effective_start: string | null;
  effective_end: string | null;
  renewal_date: string | null;
  is_current: boolean;
}


/** GET /api/v1/commitments/pickers/plan_years[?account_id=...]. */
export function fetchPlanYearPicker(
  accountId: string | null,
  opts?: { signal?: AbortSignal },
): Promise<{ plan_years: PlanYearPickerRow[] }> {
  const qs = accountId
    ? `?account_id=${encodeURIComponent(accountId)}`
    : "";
  return api.get<{ plan_years: PlanYearPickerRow[] }>(
    `/api/v1/commitments/pickers/plan_years${qs}`,
    opts,
  );
}


// ── Commitments-01c — Comments ───────────────────────────────────────────


function commentsBase(commitmentId: string): string {
  return (
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/comments`
  );
}


export function fetchCommitmentComments(
  commitmentId: string,
  opts?: { signal?: AbortSignal },
): Promise<import("./types").CommitmentCommentsListResponse> {
  return api.get<import("./types").CommitmentCommentsListResponse>(
    commentsBase(commitmentId), opts,
  );
}


export function addCommitmentComment(
  commitmentId: string,
  input: import("./types").AddCommitmentCommentInput,
): Promise<import("./types").AddCommitmentCommentResponse> {
  return api.post<import("./types").AddCommitmentCommentResponse>(
    commentsBase(commitmentId), input,
  );
}


export function deleteCommitmentComment(
  commitmentId: string, commentId: string,
): Promise<{ comment_id: string; deleted: boolean }> {
  return api.post<{ comment_id: string; deleted: boolean }>(
    `${commentsBase(commitmentId)}/${encodeURIComponent(commentId)}/delete`,
    {},
  );
}


// ── FE-048 — Commitment attachments ──────────────────────────────────────


function attachmentsBase(commitmentId: string): string {
  return (
    `/api/v1/commitments/${encodeURIComponent(commitmentId)}/attachments`
  );
}


export function fetchCommitmentAttachments(
  commitmentId: string,
  opts?: { signal?: AbortSignal },
): Promise<import("./types").CommitmentAttachmentsListResponse> {
  return api.get<import("./types").CommitmentAttachmentsListResponse>(
    attachmentsBase(commitmentId), opts,
  );
}


export function linkCommitmentAttachment(
  commitmentId: string, fileId: string,
): Promise<import("./types").CommitmentAttachmentLinkResponse> {
  return api.post<import("./types").CommitmentAttachmentLinkResponse>(
    `${attachmentsBase(commitmentId)}/link`,
    { file_id: fileId },
  );
}


export function finalizeCommitmentAttachment(
  commitmentId: string,
  payload: {
    filename: string;
    content_type: string;
    size_bytes: number;
    storage_key: string;
    hash_sha256_hex?: string;
    folder?: string;
  },
): Promise<import("./types").CommitmentAttachmentLinkResponse> {
  return api.post<import("./types").CommitmentAttachmentLinkResponse>(
    `${attachmentsBase(commitmentId)}/upload/finalize`,
    payload,
  );
}


export function unlinkCommitmentAttachment(
  commitmentId: string, linkId: string,
): Promise<{ link_id: string; unlinked: boolean }> {
  return api.post<{ link_id: string; unlinked: boolean }>(
    `${attachmentsBase(commitmentId)}/${encodeURIComponent(linkId)}/unlink`,
    {},
  );
}


export function previewCommitmentAttachment(
  commitmentId: string, linkId: string,
  opts?: {
    signal?: AbortSignal;
    /** When ``"attachment"`` the backend returns a presigned URL with
     * ``Content-Disposition: attachment`` so the browser saves the
     * file instead of previewing it inline. Default is inline. */
    disposition?: "inline" | "attachment";
  },
): Promise<import("./types").CommitmentAttachmentPreviewResponse> {
  const path = `${attachmentsBase(commitmentId)}`
    + `/${encodeURIComponent(linkId)}/preview`;
  const url = opts?.disposition === "attachment"
    ? `${path}?disposition=attachment`
    : path;
  return api.get<import("./types").CommitmentAttachmentPreviewResponse>(
    url,
    opts?.signal ? { signal: opts.signal } : undefined,
  );
}
